<template>
  <form class="px-6 pb-5 pt-2 mt-5" @submit.prevent>
    <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-2">
      <div class="flex flex-col gap-1">
        <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">{{ selectMonthLabel }}</label>
        <select
          v-model="form.selectedMonth"
          :disabled="filtersLocked"
          class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-slate-100"
        >
          <option value="" disabled>Select Month</option>
          <option
            v-for="month in monthOptions"
            :key="month.value"
            :value="month.value"
          >
            {{ month.label }}
          </option>
        </select>
      </div>

      <div v-if="mode === 'payroll' && !hideDateRange" class="flex flex-col gap-1 sm:col-span-2">
        <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">{{ dateRangeLabel }}</label>
        <select
          v-model="selectedDateRange"
          :disabled="filtersLocked || isLoadingDateRanges"
          class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-slate-100"
        >
          <option value="" disabled>
            {{ isLoadingDateRanges ? 'Loading date ranges...' : attendanceDateRanges.length === 0 ? 'No saved date ranges' : 'Select Date Range' }}
          </option>
          <option
            v-for="range in attendanceDateRanges"
            :key="`${range.fromDate}|${range.toDate}`"
            :value="`${range.fromDate}|${range.toDate}`"
          >
            {{ formatRangeDisplayLabel(range.fromDate, range.toDate) }}
          </option>
        </select>
      </div>

      <template v-if="mode !== 'payroll'">
      <div class="flex flex-col gap-1">
        <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">From Date</label>
        <input
          v-model="form.fromDate"
          type="date"
          :disabled="filtersLocked"
          class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-slate-100"
        />
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">{{ toDateLabel }}</label>
        <input
          v-model="form.toDate"
          type="date"
          :disabled="filtersLocked"
          class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-slate-100"
        />
      </div>
      </template>

      <div class="flex flex-col gap-1">
        <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Select Branch</label>
        <select
          v-model="form.selectedBranchId"
          :disabled="isLoadingBranches || filtersLocked || hasFixedBranchSelection"
          class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-slate-100"
          required
        >
          <option value="" disabled>
            {{ isLoadingBranches ? 'Loading branches...' : effectiveBranchOptions.length === 0 ? 'No branches available' : 'Select Branch' }}
          </option>
          <option
            v-for="option in effectiveBranchOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </div>

      <div v-if="mode !== 'attendance'" class="flex flex-col gap-1">
        <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Select Entity</label>
        <select
          v-model="form.selectEntity"
          :disabled="!form.selectedBranchId || isAllBranchesScope || filtersLocked"
          class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-slate-100"
        >
          <option value="" disabled>Select Entity</option>
          <option value="all">All</option>
          <option value="by_department">By Department</option>
          <option value="by_name">By Name</option>
        </select>
      </div>

      <div v-if="mode === 'attendance' || showDepartmentSelect" class="flex flex-col gap-1">
        <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Select Department</label>
        <select
          v-model="form.departmentId"
          :disabled="!form.selectedBranchId || isAllBranchesScope || isLoadingDepartments || filtersLocked"
          class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-slate-100"
        >
          <option value="" disabled>
            {{ isLoadingDepartments ? 'Loading departments...' : departmentOptions.length === 0 ? 'No active departments' : 'Select Department' }}
          </option>
          <option
            v-for="dep in departmentOptions"
            :key="dep.id"
            :value="dep.id"
          >
            {{ dep.name }}
          </option>
        </select>
      </div>

      <div v-if="mode !== 'attendance' && showSearchByName" class="flex flex-col gap-1">
        <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Search by name</label>
        <input
          v-model.trim="form.searchName"
          type="text"
          placeholder="Search by name"
          :disabled="!form.selectedBranchId || filtersLocked"
          class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-slate-100"
        />
      </div>

      <div v-if="mode !== 'attendance' && showSearchByName && props.showSearchResultsTable" class="mc-by-name-results lg:col-span-4">
        <div v-if="isSearchingUsers" class="py-6 flex justify-center items-center">
          <div class="h-6 w-6 border-2 border-[#0d4169]/30 border-t-transparent rounded-full animate-spin"></div>
        </div>
        <div v-else class="w-full overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table class="mc-user-search-table w-full min-w-[520px] border-collapse text-sm">
            <thead>
              <tr>
                <th>Full Name</th>
                <th>User name</th>
                <th>Sex</th>
                <th>Role</th>
                <th class="num">Salary</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in searchResults"
                :key="row.id"
                class="hover:bg-slate-50 transition-colors border-t border-slate-200"
              >
                <td>{{ row.fullName || '—' }}</td>
                <td>{{ row.username || '—' }}</td>
                <td>{{ row.sex || '—' }}</td>
                <td>{{ row.role || '—' }}</td>
                <td class="num">{{ fmtSalary(row.salary) }}</td>
              </tr>
              <tr v-if="!form.searchName">
                <td colspan="5" class="empty-cell">Enter a name to search.</td>
              </tr>
              <tr v-else-if="searchResults.length === 0">
                <td colspan="5" class="empty-cell">No active users found.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </form>
</template>

<script setup>
import { computed, onMounted, defineProps, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useMonthlyConfigurationFilters } from './useMonthlyConfigurationFilters.js'
import { useMonthlyConfigurationBranches } from './useMonthlyConfigurationBranches.js'
import { MAIN_COMPANY_SCOPE_VALUE } from './monthlyConfigurationScope.js'
import { apiFetch } from '../../../utils/api'
import {
  ETHIOPIAN_MONTH_DAYS,
  ETHIOPIAN_MONTH_OPTIONS,
  formatMonthOptionWithAmharic
} from '../../../utils/ethiopianMonth'

const props = defineProps({
  filtersLocked: {
    type: Boolean,
    default: false
  },
  mode: {
    type: String,
    default: 'default'
  },
  showSearchResultsTable: {
    type: Boolean,
    default: true
  },
  apiBase: {
    type: String,
    default: '/finance'
  },
  hideDateRange: {
    type: Boolean,
    default: false
  }
})

const monthlyConfigurationApiPath = computed(() => `${props.apiBase}/monthly-configuration`)

const monthOptions = computed(() =>
  ETHIOPIAN_MONTH_OPTIONS.map((month) => ({
    value: month.value,
    label: formatMonthOptionWithAmharic(month)
  }))
)

const {
  form,
  showDepartmentSelect,
  showSearchByName,
  departmentOptions,
  isLoadingDepartments,
  loadDepartments,
  totalDays,
  searchResults,
  isSearchingUsers
} = useMonthlyConfigurationFilters()

const { branchSelectOptions, isLoadingBranches, loadBranches, ALL_BRANCHES_SCOPE_VALUE } = useMonthlyConfigurationBranches()

const selectMonthLabel = computed(() => {
  // Ethiopian business months are always 30 days (same as payroll).
  if (form.selectedMonth) {
    return `Select Month (${ETHIOPIAN_MONTH_DAYS} Days)`
  }
  return 'Select Month'
})

const toDateLabel = computed(() => {
  if (form.fromDate && form.toDate && totalDays.value > 0) {
    return `To Date (${totalDays.value} Days)`
  }
  return 'To Date'
})

const dateRangeLabel = computed(() => {
  if (form.fromDate && form.toDate && totalDays.value > 0) {
    return `Date Range (${totalDays.value} Days)`
  }
  return 'Date Range'
})

const attendanceDateRanges = ref([])
const isLoadingDateRanges = ref(false)
const selectedDateRange = ref('')

function formatRangeDisplayLabel(fromDate, toDate) {
  if (!fromDate || !toDate) return ''
  return `${fromDate} - ${toDate}`
}

function resolvePayrollBranchQueryParam() {
  const scope = String(form.selectedBranchId || '').trim()
  if (!scope || scope === String(ALL_BRANCHES_SCOPE_VALUE)) return ''
  if (scope === String(MAIN_COMPANY_SCOPE_VALUE)) return ''
  const branchId = Number(scope)
  return Number.isFinite(branchId) && branchId > 0 ? String(branchId) : ''
}

async function loadAttendanceDateRanges() {
  if (props.mode !== 'payroll' || props.hideDateRange) return
  isLoadingDateRanges.value = true
  try {
    const params = new URLSearchParams()
    const branchParam = resolvePayrollBranchQueryParam()
    if (branchParam) params.set('branchId', branchParam)
    const query = params.toString()
    const response = await apiFetch(
      `${monthlyConfigurationApiPath.value}/attendance-date-ranges${query ? `?${query}` : ''}`,
      { method: 'GET' }
    )
    if (!response.ok) {
      attendanceDateRanges.value = []
      return
    }
    const data = await response.json().catch(() => [])
    attendanceDateRanges.value = Array.isArray(data) ? data : []
    if (
      selectedDateRange.value &&
      !attendanceDateRanges.value.some(
        (range) => `${range.fromDate}|${range.toDate}` === selectedDateRange.value
      )
    ) {
      selectedDateRange.value = ''
      form.fromDate = ''
      form.toDate = ''
    }
  } catch (err) {
    console.error('Error loading attendance date ranges for payroll:', err)
    attendanceDateRanges.value = []
  } finally {
    isLoadingDateRanges.value = false
  }
}

const store = useStore()
const currentUser = computed(() => store.getters.currentUser || store.state.user || {})
const tokenBranchId = computed(() => {
  const raw =
    currentUser.value?.branchId ??
    currentUser.value?.branch_id ??
    currentUser.value?.branch?.id ??
    null
  if (raw === null || raw === undefined || String(raw).trim() === '') return ''
  return String(raw).trim()
})
const tokenBranchName = computed(() => {
  const fromBranch = currentUser.value?.branch?.name
  if (fromBranch) return String(fromBranch)
  return ''
})
const hasFixedBranchSelection = computed(() => String(tokenBranchId.value || '').trim() !== '')
const effectiveBranchOptions = computed(() => {
  const forcedBranchId = tokenBranchId.value
  if (!forcedBranchId) return branchSelectOptions.value

  const matched = branchSelectOptions.value.find(
    (option) => String(option.value || '').trim() === forcedBranchId
  )
  if (matched) {
    return [{ value: forcedBranchId, label: matched.label || tokenBranchName.value || 'Branch' }]
  }
  return [{ value: forcedBranchId, label: tokenBranchName.value || 'Branch' }]
})

const isAllBranchesScope = computed(() => String(form.selectedBranchId || '') === String(ALL_BRANCHES_SCOPE_VALUE))

function fmtSalary(v) {
  const n = Number(v)
  if (!Number.isFinite(n)) return '—'
  return n.toFixed(2)
}

onMounted(() => {
  loadBranches()
  if (tokenBranchId.value) {
    form.selectedBranchId = tokenBranchId.value
  }
  if (form.selectEntity === 'by_department' && form.selectedBranchId) {
    loadDepartments(form.selectedBranchId)
  }
  if (props.mode === 'payroll' && !props.hideDateRange) {
    loadAttendanceDateRanges()
  }
  if (props.mode === 'payroll' && props.hideDateRange) {
    selectedDateRange.value = ''
    form.fromDate = ''
    form.toDate = ''
  }
})

watch(
  tokenBranchId,
  (forcedBranchId) => {
    const fixed = String(forcedBranchId || '').trim()
    if (fixed && String(form.selectedBranchId || '').trim() !== fixed) {
      form.selectedBranchId = fixed
    }
  },
  { immediate: true }
)

watch(showDepartmentSelect, (show) => {
  if (show && form.selectedBranchId) {
    loadDepartments(form.selectedBranchId)
  }
})

watch(
  () => form.selectedBranchId,
  (selected) => {
    const fixed = String(tokenBranchId.value || '').trim()
    if (fixed && String(selected || '').trim() !== fixed) {
      form.selectedBranchId = fixed
    }
    if (props.mode === 'payroll' && !props.hideDateRange) {
      loadAttendanceDateRanges()
    }
  }
)

watch(selectedDateRange, (val) => {
  if (props.mode !== 'payroll' || props.hideDateRange) return
  if (!val) {
    if (form.fromDate || form.toDate) {
      form.fromDate = ''
      form.toDate = ''
    }
    return
  }
  const [fromDate, toDate] = String(val).split('|')
  if (!fromDate || !toDate) return
  if (form.fromDate !== fromDate || form.toDate !== toDate) {
    form.fromDate = fromDate
    form.toDate = toDate
  }
})

watch(
  () => [form.fromDate, form.toDate],
  ([fromDate, toDate]) => {
    if (props.mode !== 'payroll' || props.hideDateRange) return
    if (!fromDate && !toDate) {
      if (selectedDateRange.value) selectedDateRange.value = ''
      return
    }
    if (fromDate && toDate) {
      const next = `${fromDate}|${toDate}`
      if (selectedDateRange.value !== next) selectedDateRange.value = next
    }
  }
)

watch(
  () => props.hideDateRange,
  (hidden) => {
    if (!hidden || props.mode !== 'payroll') return
    selectedDateRange.value = ''
    form.fromDate = ''
    form.toDate = ''
  }
)

// Attendance tab uses department-focused filtering only.
watch(
  () => [props.mode, form.selectedBranchId],
  ([mode]) => {
    if (mode !== 'attendance') return
    // Force department mode (but keep the entity dropdown hidden).
    form.selectEntity = 'by_department'
  },
  { immediate: true }
)
</script>

<style scoped>
.mc-by-name-results {
  width: 100%;
  min-width: 0;
}

.mc-user-search-table th,
.mc-user-search-table td {
  padding: 0.45rem 0.65rem;
  text-align: left;
  white-space: nowrap;
}

.mc-user-search-table thead tr {
  background: #f1f5f9;
}

.mc-user-search-table th {
  font-size: 0.72rem;
  font-weight: 700;
  color: #1e293b;
}

.mc-user-search-table td {
  font-size: 0.78rem;
  color: #334155;
}

.mc-user-search-table th.num,
.mc-user-search-table td.num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.mc-user-search-table .empty-cell {
  text-align: center;
  color: #94a3b8;
  font-style: italic;
  padding: 1.25rem 0.75rem;
  white-space: normal;
}
</style>
